import React, { useEffect, useMemo, useState } from "react";
import { Popover } from "antd";
import styled, { keyframes } from "styled-components";
import { Avatar } from "./Avatar";
import { pulseDarker } from "../tables/LoadingTableCell";
import { ROUTES } from "../../routes/routeNames";
import { toSlug } from "../../utils/formattingUtils";

const BadgeList = styled.div`
  display: flex;
  align-items: center;
  width: 140px;
  height: 32px;
  padding: 2px 0;
`;

const FundBadge = styled.div<{ $backgroundColor: string }>`
  display: inline-flex;
  align-items: center;
  width: 140px;
  height: 28px;
  box-sizing: border-box;
  gap: 4px;
  padding: 0 8px 0 0;
  border-radius: 999px;
  background-color: ${({ $backgroundColor }): string => $backgroundColor};
  color: #34505a;
  font-family: "Apercu";
  font-size: 11px;
  line-height: 14px;
  overflow: hidden;
  transition:
    transform 160ms ease,
    box-shadow 160ms ease;

  &:hover,
  &:focus-within {
    transform: translateY(-1px) scale(1.01);
    box-shadow: 0 3px 8px rgba(52, 80, 90, 0.14);
  }
`;

const FundLink = styled.a`
  display: inline-flex;
  flex: 1;
  align-items: center;
  min-width: 0;
  height: 100%;
  gap: 4px;
  overflow: hidden;
  color: inherit;
  text-decoration: none;

  &:hover,
  &:focus-visible,
  &:active,
  &:visited {
    color: inherit;
    text-decoration: none;
  }

  &:focus-visible {
    outline: none;
  }
`;

const ManagerStack = styled.span`
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  height: 24px;

  > span {
    width: 15px;
  }

  > span:last-child {
    width: 24px;
  }

  > span + span {
    margin-left: -5px;
  }
`;

const ManagerAvatar = styled.span<{ $zIndex: number }>`
  position: relative;
  z-index: ${({ $zIndex }): number => $zIndex};
  display: inline-flex;

  > div {
    margin-right: 0;
  }

  .ant-avatar {
    border: 0 !important;
  }
`;

const fundNameEnter = keyframes`
  from {
    opacity: 0;
    transform: translateX(6px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const FundName = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  animation: ${fundNameEnter} 180ms ease-out;
`;

const OverflowCount = styled.button`
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 18px;
  padding: 0 5px;
  border: 0;
  border-radius: 9px;
  background: rgba(255, 255, 255, 0.72);
  color: #34505a;
  font: inherit;
  line-height: 18px;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    background: rgba(255, 255, 255, 0.96);
    outline: none;
  }
`;

const FundPopover = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: max-content;
  min-width: 260px;
  max-width: min(440px, calc(100vw - 48px));
`;

const ManagerGroup = styled.a`
  display: grid;
  grid-template-columns: 24px minmax(80px, auto) minmax(0, 1fr);
  align-items: center;
  gap: 8px;
  color: inherit;
  text-decoration: none;

  &:focus-visible {
    outline: 1px solid #9aabb1;
    outline-offset: 3px;
  }

  & + & {
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px solid #edf0f1;
  }
`;

const PopoverManagerName = styled.span`
  overflow: hidden;
  color: #34505a;
  font-family: "Apercu";
  font-size: 11px;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const PopoverFundName = styled.span`
  overflow: hidden;
  color: #52666d;
  font-family: "Apercu";
  font-size: 11px;
  line-height: 18px;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const LOADING_TINT = "#f0f2f3";

const LoadingLabel = styled.span`
  flex: 1;
  height: 8px;
  margin-right: 2px;
  border-radius: 4px;
  animation: ${pulseDarker} 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite;
`;

const getFallbackTint = (managerName: string): string => {
  const nameValue = Array.from(managerName).reduce(
    (total, character) => total + character.charCodeAt(0),
    0,
  );
  const hue = nameValue % 360;

  return `hsl(${hue}, 50%, 94%)`;
};

const getFundLabel = (fundName: string): string => {
  const nameWithoutFund = fundName
    .replace(/\bfunds?\b/gi, "")
    .replace(/\s{2,}/g, " ")
    .trim();
  return nameWithoutFund || fundName;
};

const getLogoTint = (image: HTMLImageElement): string | null => {
  const canvas = document.createElement("canvas");
  const size = 32;
  canvas.width = size;
  canvas.height = size;
  const context = canvas.getContext("2d", { willReadFrequently: true });
  if (!context) return null;

  context.drawImage(image, 0, 0, size, size);
  const pixels = context.getImageData(0, 0, size, size).data;
  const colorCounts = new Map<string, number>();

  for (let index = 0; index < pixels.length; index += 16) {
    const red = pixels[index];
    const green = pixels[index + 1];
    const blue = pixels[index + 2];
    const alpha = pixels[index + 3];
    const isNearWhite = red > 235 && green > 235 && blue > 235;
    const isNearBlack = red < 25 && green < 25 && blue < 25;

    if (alpha < 180 || isNearWhite || isNearBlack) continue;

    const quantizedColor = `${Math.round(red / 24) * 24},${
      Math.round(green / 24) * 24
    },${Math.round(blue / 24) * 24}`;
    colorCounts.set(quantizedColor, (colorCounts.get(quantizedColor) || 0) + 1);
  }

  const dominantColor = Array.from(colorCounts.entries()).sort(
    ([firstColor, firstCount], [secondColor, secondCount]) => {
      const firstChannels = firstColor.split(",").map(Number);
      const secondChannels = secondColor.split(",").map(Number);
      const firstChroma =
        Math.max(...firstChannels) - Math.min(...firstChannels);
      const secondChroma =
        Math.max(...secondChannels) - Math.min(...secondChannels);

      return (
        secondCount * (1 + secondChroma / 72) -
        firstCount * (1 + firstChroma / 72)
      );
    },
  )[0]?.[0];

  return dominantColor ? `rgba(${dominantColor}, 0.14)` : null;
};

export interface FundManagerBadgeProps {
  fundId: string;
  fundName: string;
  managerImage?: string;
  managerName?: string;
  isLoading?: boolean;
}

export const FundManagerBadgeList = ({
  items,
}: FundManagerBadgeListProps): JSX.Element => {
  const [activeIndex, setActiveIndex] = useState(0);
  const isLoading = items.some((item) => item.isLoading);
  const managerGroups = useMemo(() => groupFundsByManager(items), [items]);
  const activeItem = items[activeIndex] || items[0];
  const visibleManagers = activeItem
    ? [...managerGroups]
        .sort((firstGroup, secondGroup) => {
          const activeManagerKey = getManagerKey(activeItem);
          return (
            Number(secondGroup.key === activeManagerKey) -
            Number(firstGroup.key === activeManagerKey)
          );
        })
        .slice(0, 3)
        .map((group) => group.manager)
    : [];

  useEffect(() => {
    if (activeIndex >= items.length) setActiveIndex(0);
  }, [activeIndex, items.length]);

  if (!activeItem) return <BadgeList />;

  if (isLoading) {
    return (
      <BadgeList>
        <FundManagerBadge
          item={{ fundId: "", fundName: "", isLoading: true }}
        />
      </BadgeList>
    );
  }

  const showNextFund = (): void => {
    setActiveIndex((currentIndex) => (currentIndex + 1) % items.length);
  };

  return (
    <Popover
      content={<FundManagerPopover groups={managerGroups} />}
      placement="bottomLeft"
      trigger={["hover", "focus"]}
    >
      <BadgeList>
        <FundManagerBadge
          key={`${activeIndex}-${activeItem.fundName}`}
          item={activeItem}
          managers={visibleManagers}
          overflowCount={items.length - 1}
          onShowNext={showNextFund}
        />
      </BadgeList>
    </Popover>
  );
};

interface ManagerGroupValue {
  key: string;
  manager: FundManagerBadgeProps;
  funds: FundManagerBadgeProps[];
}

const getManagerKey = (item: FundManagerBadgeProps): string =>
  item.managerImage || item.managerName || "manager-unavailable";

const groupFundsByManager = (
  items: FundManagerBadgeProps[],
): ManagerGroupValue[] => {
  const groups = new Map<string, ManagerGroupValue>();

  items.forEach((item) => {
    const key = getManagerKey(item);
    const group = groups.get(key);

    if (group) {
      group.funds.push(item);
      return;
    }

    groups.set(key, { key, manager: item, funds: [item] });
  });

  return Array.from(groups.values());
};

const getFundHref = (fundId: string): string =>
  `${ROUTES.SCREENING_TOOL}/${toSlug(fundId)}`;

const FundManagerPopover = ({
  groups,
}: {
  groups: ManagerGroupValue[];
}): JSX.Element => (
  <FundPopover>
    {groups.flatMap(({ key, manager, funds }) =>
      funds.map((fund, index) => (
        <ManagerGroup
          key={`${key}-${fund.fundName}-${index}`}
          href={getFundHref(fund.fundId)}
          target="_blank"
          rel="noopener noreferrer"
        >
          <Avatar
            username={manager.managerName || manager.fundName}
            image={manager.managerImage}
            size="small"
          />
          <PopoverManagerName>
            {manager.managerName || "Manager unavailable"}
          </PopoverManagerName>
          <PopoverFundName title={fund.fundName}>
            {fund.fundName}
          </PopoverFundName>
        </ManagerGroup>
      )),
    )}
  </FundPopover>
);

const FundManagerBadge = ({
  item,
  managers = [item],
  overflowCount = 0,
  onShowNext,
}: {
  item: FundManagerBadgeProps;
  managers?: FundManagerBadgeProps[];
  overflowCount?: number;
  onShowNext?: () => void;
}): JSX.Element => {
  const tintSeed = item.managerName || item.fundName;
  const [backgroundColor, setBackgroundColor] = useState<string>(() =>
    getFallbackTint(tintSeed),
  );

  useEffect(() => {
    if (!item.managerImage) return;

    const colorSample = new Image();
    colorSample.crossOrigin = "anonymous";
    colorSample.onload = (): void => {
      try {
        setBackgroundColor(
          getLogoTint(colorSample) || getFallbackTint(tintSeed),
        );
      } catch {
        setBackgroundColor(getFallbackTint(tintSeed));
      }
    };
    colorSample.onerror = (): void =>
      setBackgroundColor(getFallbackTint(tintSeed));
    colorSample.src = item.managerImage;
  }, [item.managerImage, tintSeed]);

  if (item.isLoading) {
    return (
      <FundBadge $backgroundColor={LOADING_TINT}>
        <Avatar username="" size="small" isLoading={true} />
        <LoadingLabel aria-label="Loading fund" />
      </FundBadge>
    );
  }

  return (
    <FundBadge $backgroundColor={backgroundColor}>
      <FundLink
        href={getFundHref(item.fundId)}
        target="_blank"
        rel="noopener noreferrer"
        title={
          item.managerName
            ? `${item.managerName}: ${item.fundName}`
            : item.fundName
        }
      >
        <ManagerStack
          aria-label={`${managers.length} fund manager${managers.length === 1 ? "" : "s"}`}
        >
          {managers.map((manager, index) => (
            <ManagerAvatar
              key={`${manager.managerImage}-${manager.managerName}-${index}`}
              $zIndex={managers.length - index}
            >
              <Avatar
                username={manager.managerName || manager.fundName}
                image={manager.managerImage}
                size="small"
              />
            </ManagerAvatar>
          ))}
        </ManagerStack>
        <FundName>{getFundLabel(item.fundName)}</FundName>
      </FundLink>
      {overflowCount > 0 && (
        <OverflowCount
          type="button"
          aria-label={`Show next fund. ${overflowCount} more fund${overflowCount === 1 ? "" : "s"}`}
          onClick={(event): void => {
            event.stopPropagation();
            onShowNext?.();
          }}
        >
          +{overflowCount}
        </OverflowCount>
      )}
    </FundBadge>
  );
};

interface FundManagerBadgeListProps {
  items: FundManagerBadgeProps[];
}
